import { UserUpdateRequest } from "@/clients/models/UserUpdateRequest";
import { StepsButtons } from "@/components/button/steps-button";
import { QueryKeys } from "@/constants/query-keys";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useGetGoals } from "@/hooks/requests/goals/use-get-goals";
import { useUpdateGoals } from "@/hooks/requests/goals/use-update-goals";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { UserStatusSchema } from "@/schemas/user-schema";
import { setProfileContext } from "@/utils/sentry/tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { IonSkeletonText } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AnimatedOnboardingContainer } from "../../components/animated-onboarding-container";
import DateModalSelect from "../../components/date-modal-select";
import GoalModalSelect from "../../components/goal-modal-select";
import HeightModalSelect from "../../components/height-modal-select";
import SexModalSelect from "../../components/sex-modal-select";
import WeightInputUnitFields from "../../components/weight-input-unit-fields";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const OnboardingUserStatusPage = () => {
  const { info, navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { user, isLoading: isUserLoading } = useGetUser();
  const { bodyStats, isLoading: isBodyStatsLoading } = useGetBodyStats();
  const { goals, isLoading: isGoalsLoading } = useGetGoals();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating: isUserUpdating } = useUpdateUser();
  const { updateBodyStatsAsync, isUpdating: isBodyStatsUpdating } = useUpdateBodyStats();
  const { updateGoalsAsync, isUpdating: isGoalsUpdating } = useUpdateGoals();
  const { showError } = useToast();

  const personalInfo = user?.personalInfo;

  const initialValues = useMemo(() => {
    return {
      sex: personalInfo?.sex ?? "male",
      dob: personalInfo?.dob ?? "2000-01-01",
      height: bodyStats?.height ?? "164",
      heightUnit: bodyStats?.heightUnit ?? "cm",
      currentWeight: bodyStats?.currentWeight ?? "60",
      currentWeightUnit: bodyStats?.currentWeightUnit ?? "kg",
      goalsWithNutriCoach: goals?.goalsWithNutriCoach ?? "lose-weight",
    } satisfies z.infer<typeof UserStatusSchema>;
  }, [
    personalInfo?.sex,
    personalInfo?.dob,
    bodyStats?.height,
    bodyStats?.heightUnit,
    bodyStats?.currentWeight,
    bodyStats?.currentWeightUnit,
    goals?.goalsWithNutriCoach,
  ]);

  const form = useForm<z.infer<typeof UserStatusSchema>>({
    resolver: zodResolver(UserStatusSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialValues,
  });

  const didHydrateFormRef = useRef(false);

  useEffect(() => {
    if (isUserLoading || isBodyStatsLoading || isGoalsLoading) return;
    if (didHydrateFormRef.current) return;
    form.reset(initialValues);
    didHydrateFormRef.current = true;
  }, [form, initialValues, isUserLoading, isBodyStatsLoading, isGoalsLoading]);

  const {
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  async function handleFormSubmit({
    sex,
    dob,
    height,
    heightUnit,
    currentWeight,
    currentWeightUnit,
    goalsWithNutriCoach,
  }: z.infer<typeof UserStatusSchema>) {
    try {
      const validatedValues = UserStatusSchema.parse({
        sex,
        dob: format(new Date(dob), "yyyy-MM-dd"),
        height,
        heightUnit,
        currentWeight,
        currentWeightUnit,
        goalsWithNutriCoach,
      });

      const userDataBody: UserUpdateRequest = {
        firstName: personalInfo?.firstName ?? "",
        lastName: personalInfo?.lastName ?? "",
        dob: validatedValues.dob ?? personalInfo?.dob ?? "",
        sex: validatedValues.sex ?? personalInfo?.sex ?? "",
      };

      await Promise.all([
        preloadNextRoute(),
        updateUserAsync(userDataBody),
        updateBodyStatsAsync({
          height: validatedValues.height ?? bodyStats?.height,
          heightUnit: validatedValues.heightUnit ?? bodyStats?.heightUnit ?? "cm",
          currentWeight: validatedValues.currentWeight ?? bodyStats?.currentWeight,
          currentWeightUnit: validatedValues.currentWeightUnit ?? bodyStats?.currentWeightUnit,
          targetWeight: validatedValues.currentWeight ?? bodyStats?.currentWeight,
          targetWeightUnit: validatedValues.currentWeightUnit ?? bodyStats?.currentWeightUnit,
          startingWeight: bodyStats?.startingWeight ?? validatedValues.currentWeight,
          startingWeightUnit: bodyStats?.startingWeightUnit ?? validatedValues.currentWeightUnit,
        }),
        updateGoalsAsync({
          goalsWithNutriCoach: validatedValues.goalsWithNutriCoach,
          specificWeightGoals: goals?.specificWeightGoals ?? "lose-weight",
        }),
      ]);

      setSmartlookOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      setMixpanelOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      navigateToNextStep();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GOALS] });
      setProfileContext(QueryKeys.USER, userDataBody);
      setProfileContext(QueryKeys.GOALS, {
        goalsWithNutriCoach: validatedValues.goalsWithNutriCoach,
        specificWeightGoals: goals?.specificWeightGoals ?? "lose-weight",
      });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unexpected error occurred");
      }
    }
  }

  if (isUserLoading || isBodyStatsLoading || isGoalsLoading) {
    return (
      <AnimatedOnboardingContainer title="So, tell me a bit about you!">
        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-20" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>

        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-28" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>

        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-16" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>

        <div className="flex w-full gap-3">
          <div className="flex flex-1 flex-col gap-2">
            <IonSkeletonText className="h-5 w-16" animated />
            <IonSkeletonText className="h-12 w-full rounded-lg" animated />
          </div>

          <div className="flex flex-1 flex-col gap-2">
            <IonSkeletonText className="h-5 w-28" animated />
            <IonSkeletonText className="h-12 w-full rounded-lg" animated />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-32" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>
      </AnimatedOnboardingContainer>
    );
  }

  return (
    <AnimatedOnboardingContainer
      title="So, tell me a bit about you!"
      handleSubmit={handleSubmit(handleFormSubmit)}
    >
      <SexModalSelect form={form} name="sex" error={errors.sex?.message} />

      <DateModalSelect form={form} name="dob" error={errors.dob?.message} />

      <HeightModalSelect
        form={form}
        valueName="height"
        unitName="heightUnit"
        error={errors.height?.message}
      />

      <WeightInputUnitFields
        form={form}
        valueName="currentWeight"
        unitName="currentWeightUnit"
        valueError={errors.currentWeight?.message}
        unitError={errors.currentWeightUnit?.message}
      />
      <GoalModalSelect
        form={form}
        name="goalsWithNutriCoach"
        error={errors.goalsWithNutriCoach?.message}
      />

      <StepsButtons
        type="submit"
        disabled={isUserUpdating || isBodyStatsUpdating || isGoalsUpdating || !isValid}
      />
    </AnimatedOnboardingContainer>
  );
};

export default OnboardingUserStatusPage;
